import React, { useState, useEffect, useRef, useMemo } from "react";
import "./VendorAuditDashboardV2.css";
import VendorAuditDashboardFilter from "../VendorAudit/VendorAuditDashboardFilter";

import ComplianceItemStatus from "./ComplianceItemStatus";
import VendorPerformanceMatrix from "./VendorPerformanceMatrix";
import RecentAuditReports from "./RecentAuditReports";
import {
  useGetComplianceItemStatus,
  useGetVendorPerformanceMatrix,
} from "../../../backend/vendorAuditV2";
import { useGetAllVendorAudits } from "../../../backend/query";
import { useAuditReportForVendor } from "../../../backend/exports";
import { downloadFileContent } from "../../../utils/common";
import { toast } from "react-toastify";
import { navigate } from "raviger";
import * as api from "../../../backend/request";

// ── Component ──

const VendorAuditDashboardV2 = () => {
  const currentYear = new Date().getFullYear();

  const [locationFilters, setLocationFilter] = useState<any>({
    year: currentYear,
  });
  const [previousFilters, setPreviousFilters] = useState<any>();
  const [selectedReport, setSelectedReport] = useState<any>(null);

  const previousFiltersRef = useRef();
  previousFiltersRef.current = previousFilters;

  // Helper to convert month number (1-12) to month name string
  const getMonthName = (monthNum: number) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthNum - 1];
  };

  const getAPIPayload = (filters: any) => {
    if (!filters) return null;

    const payload: any = {
      company_id: filters.company ? [filters.company] : (filters.all_company_ids || []),
    };

    if (filters.year) {
      payload.year = String(filters.year);
    }
    if (filters.associate_company) {
      payload.associate_company_id = [filters.associate_company];
    }
    if (filters.location_id) {
      payload.location_id = [filters.location_id];
    }
    if (filters.vendor_category_id) {
      payload.vendor_category_id = [filters.vendor_category_id];
    }
    if (filters.vendor_registration_id) {
      payload.vendor_registration_id = [filters.vendor_registration_id];
    }
    if (filters.month) {
      payload.month = getMonthName(filters.month);
    }

    return payload;
  };

  const apiPayload = getAPIPayload(locationFilters);
  const isEnabled = Boolean(locationFilters && (locationFilters.company || locationFilters.all_company_ids));

  const { complianceItemStatus, isFetching: fetchingStatus } = useGetComplianceItemStatus(apiPayload, isEnabled);
  const { performanceMatrix, isFetching: fetchingMatrix } = useGetVendorPerformanceMatrix(apiPayload, isEnabled);

  // Recent Audit Reports - reuse existing vendor_location_audits API
  const recentAuditsPayload = useMemo(() => {
    if (!locationFilters) return null;

    const filters: any = { status_eq: 'final_audit_report_uploaded' };

    if (locationFilters.company) {
      filters.company_id_eq = locationFilters.company;
    } else if (locationFilters.all_company_ids && locationFilters.all_company_ids.length > 0) {
      filters.company_id_in = locationFilters.all_company_ids;
    }

    if (locationFilters.associate_company) {
      filters.associate_company_id_eq = locationFilters.associate_company;
    }
    if (locationFilters.location_id) {
      filters.location_id_eq = locationFilters.location_id;
    }
    if (locationFilters.vendor_category_id) {
      filters.vendor_category_id_eq = locationFilters.vendor_category_id;
    }
    if (locationFilters.vendor_registration_id) {
      filters.vendor_registration_id_eq = locationFilters.vendor_registration_id;
    }
    if (locationFilters.year) {
      filters.year_eq = String(locationFilters.year);
    }
    if (locationFilters.month) {
      filters.month_eq = getMonthName(locationFilters.month);
    }

    return {
      page: 1,
      per: 10,
      filters,
      sort_by: 'updated_at',
      sort_order: 'desc',
      include: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'final_report'],
      includes: ['company', 'associate_company', 'location', 'vendor_category', 'vendor_registration', 'final_report'],
    };
  }, [JSON.stringify(locationFilters)]);

  const { activities: recentAudits, total: recentTotal, isFetching: fetchingRecentAudits } = useGetAllVendorAudits(
    recentAuditsPayload,
    isEnabled
  );

  // Download report handler
  const { auditReportVendor, exporting: downloadingReport } = useAuditReportForVendor(
    (response: any) => {
      const filename = selectedReport && selectedReport.name
        ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1)
        : 'audit_report';
      if (response) {
        downloadFileContent({
          name: filename,
          type: response.headers["content-type"],
          content: response.data,
        });
      }
    },
    () => {
      toast.error("Failed to download report");
    }
  );

  async function handleDownloadReport(audit: any) {
    if (!audit || !audit.id) return;

    let reportData = audit.final_report;

    if (!reportData) {
      try {
        const response = await api.get(`/api/v1/vendor_location_audits/${audit.id}`, {
          include: ['final_report'],
          includes: ['final_report'],
        });
        reportData = response.data && response.data.final_report;
      } catch {
        toast.error("Failed to fetch report details");
        return;
      }
    }

    if (!reportData) {
      toast.warn("No report available");
      return;
    }

    setSelectedReport(reportData);
    const { s3_region, s3_key, s3_bucket } = reportData || {};
    if (s3_region && s3_key && s3_bucket) {
      auditReportVendor({ s3_region, s3_key, s3_bucket });
    } else {
      toast.warn("Report file not found");
    }
  }

  // Map compliance card key to audit status filter value
  const STATUS_KEY_MAP: Record<string, string> = {
    scheduled_count: "scheduled",
    preliminary_in_progress_count: "in_progress",
    preliminary_completed_count: "preliminary_completed",
    final_completed_count: "final_completed",
    dropped_audits_count: "dropped",
    total_count: "all",
  };

  function handleComplianceCardClick(key: string) {
    // Build previousFilters in the format AdminVendorLocations expects
    const state: any = {};

    if (locationFilters.company) {
      state.company = { value: locationFilters.company };
    }
    if (locationFilters.associate_company) {
      state.associateCompany = { value: locationFilters.associate_company };
    }
    if (locationFilters.location_id) {
      state.locations = [{ value: locationFilters.location_id }];
    }
    if (locationFilters.vendor_category_id) {
      state.vendorCategories = [{ value: locationFilters.vendor_category_id }];
    }
    if (locationFilters.vendor_registration_id) {
      state.vendorRegistrations = [{ value: locationFilters.vendor_registration_id }];
    }

    // Pass raw API filters for VendorAuditList to apply directly
    const initialFilters: any = {};
    if (locationFilters.company) {
      initialFilters.company_id_eq = locationFilters.company;
    } else if (locationFilters.all_company_ids && locationFilters.all_company_ids.length > 0) {
      initialFilters.company_id_in = locationFilters.all_company_ids;
    }
    if (locationFilters.associate_company) {
      initialFilters.associate_company_id_eq = locationFilters.associate_company;
    }
    if (locationFilters.location_id) {
      initialFilters.location_id_eq = locationFilters.location_id;
    }
    if (locationFilters.vendor_category_id) {
      initialFilters.vendor_category_id_eq = locationFilters.vendor_category_id;
    }
    if (locationFilters.vendor_registration_id) {
      initialFilters.vendor_registration_id_eq = locationFilters.vendor_registration_id;
    }
    state.initialFilters = initialFilters;

    // Pass audit status for VendorAuditList to pick up
    state.auditStatus = STATUS_KEY_MAP[key] || "all";

    // Pass month and year filters
    if (locationFilters.month) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      state.month = monthNames[locationFilters.month - 1];
    }
    if (locationFilters.year) {
      state.year = locationFilters.year;
    }

    navigate("/vendor-audits", { state });
  }

  function onLocationChange(event: any, filter: any = {}) {
    const updatedFilters = {
      year: currentYear,
      ...event,
    };
    setLocationFilter(updatedFilters);
    if (Object.keys(filter).length > 0) {
      const _filter = {
        ...previousFilters,
        ...filter,
      };
      setPreviousFilters(_filter);
    }
  }

  return (
    <section className="vendor-audit-v2-wrapper">
      <div className="d-flex flex-column" style={{ backgroundColor: "#F6F8FA" }}>
        {/* Page Header */}
        <div className="page-header" style={{ padding: "16px 24px 0 24px" }}>
          <h1>Vendor Audit Dashboard</h1>
          <p className="subtitle">
            Monitor audit progress, vendor compliance, and key risk indicators in real time.
          </p>
        </div>

        {/* Filter Bar */}
        <form className="p-0" style={{ margin: "0 24px" }}>
          <div className="py-3">
            <div className="d-flex align-items-center filter-bar">
              <VendorAuditDashboardFilter
                onChange={onLocationChange}
                previousFilters={previousFilters}
              />
            </div>
          </div>

        </form>
      </div>

      {/* Main Content */}
      <section className="vendor-audit-v2-content">
        {/* Section 1: Compliance Item Status */}
        <ComplianceItemStatus
          data={complianceItemStatus}
          isLoading={fetchingStatus}
          onCardClick={handleComplianceCardClick}
        />

        {/* Section 2: Vendor Performance Matrix */}
        <VendorPerformanceMatrix
          data={performanceMatrix}
          isLoading={fetchingMatrix}
        />

        {/* Section 3: Recent Audit Reports */}
        <RecentAuditReports
          data={recentAudits}
          totalCount={recentTotal}
          isLoading={fetchingRecentAudits}
          onDownload={handleDownloadReport}
        />
      </section>
    </section>
  );
};

export default VendorAuditDashboardV2;
